// Mẫu văn bản cho khách ký điện tử: dựng nội dung từ dữ liệu khách / thẻ / buổi, in kèm chữ ký
import dayjs from 'dayjs';
import { BRANCHES, staffName } from '../POS/data';
import { formatPhone } from '../POS/utils';
import { DEFAULT_CONFIG, KIND_LABEL, type CardUsage, type TreatmentCard } from '../Treatment/data';
import { cardBalance, cardPolicy, expiryText, installmentRows, noExpiry } from '../Treatment/logic';
import { printHtml } from '../Treatment/print';
import { SIGN_KIND, type DocSection, type SignDoc, type SignKind } from './data';
import type { Visit } from './logic';

const money = (n: number) => `${new Intl.NumberFormat('vi-VN').format(Math.round(n || 0))}đ`;
const d = (s: string) => dayjs(s).format('DD/MM/YYYY');
const dt = (s: string) => dayjs(s).format('DD/MM/YYYY HH:mm');
const branch = (id: string) => BRANCHES.find(b => b.id === id)?.name || id;

export interface SignParty { name: string; phone: string; birthday?: string | null; gender?: string }

/** Câu hỏi sàng lọc trên phiếu đồng ý thực hiện dịch vụ */
export const CONSENT_QUESTIONS = [
  { key: 'allergy', label: 'Tiền sử dị ứng (thuốc, mỹ phẩm, thức ăn)' },
  { key: 'pregnant', label: 'Đang mang thai / cho con bú' },
  { key: 'disease', label: 'Bệnh lý nền (tim mạch, tiểu đường, rối loạn đông máu…)' },
  { key: 'medicine', label: 'Đang dùng thuốc / vừa thực hiện thẩm mỹ khác trong 3 tháng' },
];

export type ConsentAnswers = Record<string, { yes: boolean; note: string }>;

const partySection = (p: SignParty): DocSection => ({
  heading: 'Thông tin khách hàng',
  lines: [
    `Họ tên: ${p.name}`,
    `Số điện thoại: ${formatPhone(p.phone) || '—'}`,
    ...(p.birthday ? [`Ngày sinh: ${d(p.birthday)}`] : []),
    ...(p.gender ? [`Giới tính: ${p.gender}`] : []),
  ],
});

export function buildSections(kind: SignKind, ctx: { party: SignParty; card?: TreatmentCard | null; usage?: CardUsage | null; answers?: ConsentAnswers }): DocSection[] {
  const { party, card, usage } = ctx;
  if (kind === 'consent') {
    const a = ctx.answers || {};
    return [
      partySection(party),
      {
        heading: 'Khai báo sức khỏe',
        lines: CONSENT_QUESTIONS.map(q => `${q.label}: ${a[q.key]?.yes ? `CÓ${a[q.key]?.note ? ` – ${a[q.key].note}` : ''}` : 'Không'}`),
      },
      {
        heading: 'Cam kết',
        lines: [
          'Tôi đã được chuyên viên tư vấn đầy đủ về quy trình, kết quả có thể đạt được, rủi ro và tác dụng phụ có thể xảy ra.',
          'Tôi khai báo trung thực tình trạng sức khỏe ở trên và chịu trách nhiệm nếu khai báo sai.',
          'Tôi tự nguyện đồng ý thực hiện các dịch vụ trong liệu trình và tuân thủ hướng dẫn chăm sóc sau điều trị.',
        ],
      },
    ];
  }
  if (kind === 'contract' && card) {
    const b = cardBalance(card);
    const pol = cardPolicy(card);
    const rows = installmentRows(card, DEFAULT_CONFIG);
    return [
      partySection(party),
      {
        heading: 'Thông tin thẻ',
        lines: [
          `Mã thẻ: ${card.id} · ${KIND_LABEL[card.kind]}`,
          `Gói: ${card.packageName} (${card.packageCode})`,
          ...(card.kind === 'session'
            ? card.lines.map(l => `• ${l.name}: ${l.total + l.bonus} buổi${l.role === 'bonus' ? ' (tặng kèm)' : ''}`)
            : [`Mệnh giá sử dụng: ${money(b.faceValue)}`]),
          `Ngày mua: ${d(card.createdAt)} tại ${branch(card.branchId)} · ${noExpiry(card.expiresAt) ? 'Không thời hạn' : `Hạn dùng: ${expiryText(card)}`}`,
        ],
      },
      {
        heading: 'Giá và thanh toán',
        lines: [
          `Giá niêm yết: ${money(card.listPrice)} · Giá bán: ${money(card.price)}`,
          ...(rows.length > 1 ? rows.map(r => `Đợt ${r.no}: ${money(r.amount)} – hạn ${d(r.dueDate)}`) : ['Thanh toán một lần']),
        ],
      },
      {
        heading: 'Điều khoản',
        lines: [
          pol.allowRefund ? `Hoàn tiền: được hoàn phần chưa sử dụng, phí hoàn ${pol.refundFeePct}% (dịch vụ tặng kèm không được quy đổi).` : 'Gói không áp dụng hoàn tiền.',
          pol.maxExtensionMonths > 0 ? `Gia hạn: tối đa ${pol.maxExtensionMonths} tháng${pol.extensionFeePerMonth ? `, phí ${money(pol.extensionFeePerMonth)}/tháng` : ', miễn phí'}.` : 'Gói không áp dụng gia hạn.',
          'Đợt thanh toán quá hạn có thể bị tạm ngưng sử dụng buổi cho đến khi hoàn tất.',
          'Thẻ chỉ dùng cho chính khách hàng đứng tên, không chuyển nhượng khi chưa có xác nhận của cơ sở.',
        ],
      },
    ];
  }
  if (kind === 'photo') {
    return [
      partySection(party),
      {
        heading: 'Nội dung đồng ý',
        lines: [
          'Tôi đồng ý để cơ sở chụp ảnh vùng điều trị trước, trong và sau liệu trình nhằm theo dõi – đánh giá kết quả.',
          'Ảnh được lưu trong hồ sơ khách hàng, chỉ nhân viên có thẩm quyền được xem.',
          'Cơ sở KHÔNG sử dụng ảnh cho mục đích quảng cáo khi chưa có thêm sự đồng ý riêng bằng văn bản của tôi.',
          'Tôi có quyền yêu cầu ngừng chụp hoặc ẩn ảnh khỏi hồ sơ bất cứ lúc nào.',
        ],
      },
    ];
  }
  if (kind === 'session' && card && usage) {
    const b = cardBalance(card);
    return [
      partySection(party),
      {
        heading: 'Buổi thực hiện',
        lines: [
          `Thẻ: ${card.packageName} (${card.id})`,
          `Thời gian: ${dt(usage.at)} tại ${branch(usage.branchId)}`,
          `Kỹ thuật viên: ${staffName(usage.staffId) || usage.staffId}`,
          ...usage.items.map(i => `• ${i.name} ×${i.qty}`),
          card.kind === 'session' ? `Còn lại sau buổi: ${b.remainingSessions}/${b.totalSessions} buổi` : `Mệnh giá còn lại: ${money(b.remainingValue)}`,
        ],
      },
      { heading: 'Xác nhận', lines: ['Tôi xác nhận đã được thực hiện đầy đủ các dịch vụ trên và đồng ý trừ vào thẻ.'] },
    ];
  }
  return [partySection(party)];
}

export const defaultTitle = (kind: SignKind, card?: TreatmentCard | null) =>
  card && (kind === 'contract' || kind === 'session') ? `${SIGN_KIND[kind].label} – ${card.packageName}` : SIGN_KIND[kind].label;

const esc = (s: string) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export function docHtml(doc: SignDoc, signature: string | null) {
  const reprint = doc.printCount > 0 ? '<div class="reprint">BẢN IN LẠI</div>' : '';
  const revoked = doc.status === 'revoked' ? `<div class="reprint" style="color:#b91c1c;border-color:#b91c1c">ĐÃ THU HỒI ${esc(doc.revoked ? `${dt(doc.revoked.at)} – ${doc.revoked.reason}` : '')}</div>` : '';
  return `<h1>KHOTHI</h1><div class="c muted">${esc(branch(doc.branchId))}</div>
    <div class="c" style="font-weight:700;margin:8px 0 2px;font-size:15px">${esc(doc.title.toUpperCase())}</div>
    <div class="c muted">Số: ${esc(doc.id)}</div>${reprint}${revoked}
    ${doc.sections.map(s => `<h2>${esc(s.heading)}</h2>${s.lines.map(l => `<div>${esc(l)}</div>`).join('')}`).join('')}
    <div class="sign"><div><i>Nhân viên chứng kiến</i><b>${esc(doc.witness)}</b></div>
    <div><i>Khách hàng ký (điện tử) lúc ${esc(dt(doc.signedAt))}</i>${signature ? `<img src="${signature}" style="max-height:90px;max-width:100%;margin-top:-40px"/>` : ''}<div><b>${esc(doc.signerName)}</b></div></div></div>
    <hr/><div class="muted">Mã xác thực (SHA-256): ${esc(doc.hash)}<br/>Thiết bị: ${esc(doc.device)}</div>`;
}

export function printDoc(doc: SignDoc, signature: string | null) {
  printHtml(doc.id, 'A4', docHtml(doc, signature));
}

/** Phiếu xác nhận một lần đến: mọi dịch vụ khách được chọn ký, KTV, giờ vào/ra, ghi chú, đánh giá */
export function visitSections(party: SignParty, v: Visit, keys: string[], extra: { note?: string; rating?: number; ratingNote?: string; now?: Date } = {}): DocSection[] {
  const items = v.items.filter(i => keys.includes(i.key));
  const late = dayjs(extra.now || new Date()).format('YYYY-MM-DD') !== v.date;
  return [
    partySection(party),
    {
      heading: 'Lần đến',
      lines: [
        `Ngày ${d(v.date)} tại ${v.branchIds.map(branch).join(', ')}`,
        `Thời gian: ${dayjs(v.firstAt).format('HH:mm')}${v.lastAt !== v.firstAt ? ` – ${dayjs(v.lastAt).format('HH:mm')}` : ''}`,
        `Kỹ thuật viên: ${Array.from(new Set(items.flatMap(i => i.staff))).join(', ') || '—'}`,
      ],
    },
    { heading: 'Dịch vụ đã thực hiện', lines: items.map(i => `• ${i.kind === 'use' ? `${i.title}: ` : ''}${i.detail}`) },
    ...(extra.note ? [{ heading: 'Ghi chú sau buổi làm', lines: [extra.note] }] : []),
    ...(extra.rating ? [{ heading: 'Khách đánh giá', lines: [`${'★'.repeat(extra.rating)}${'☆'.repeat(5 - extra.rating)} (${extra.rating}/5)${extra.ratingNote ? ` – ${extra.ratingNote}` : ''}`] }] : []),
    {
      heading: 'Xác nhận',
      lines: [
        'Tôi xác nhận đã được thực hiện đầy đủ các dịch vụ trên và đồng ý trừ vào thẻ liệu trình / hóa đơn tương ứng.',
        ...(late ? [`Ký bổ sung cho lần đến ngày ${d(v.date)}.`] : []),
      ],
    },
  ];
}

export const visitTitle = (date: string) => `${SIGN_KIND.visit.label} ngày ${d(date)}`;
